import React, { useEffect, useRef, useState } from 'react';
import { ImageRightsAPI } from '../../api/ImageRightsAPI';
import schickLogoWhite from '../../resources/images/schick-logo-white.png';
import { renderAppPreview } from './appPreview';

/** Copies a PNG to the clipboard; false where the browser cannot. */
export async function copyImage(blob) {
  if (!navigator.clipboard?.write || typeof window.ClipboardItem !== 'function') return false;
  await navigator.clipboard.write([new window.ClipboardItem({ 'image/png': blob })]);
  return true;
}

const fileName = (picture) =>
  `schick-vorschau-${String(picture.title || picture.id || 'bild').toLowerCase().replace(/[^a-z0-9äöüß]+/gi, '-').replace(/^-|-$/g, '').slice(0, 40)}.png`;

/**
 * The picture as the app shows it at the top of its information card, in a phone on
 * Schick blue, ready to paste into the organiser's DM.
 */
export default function AppPreviewDialog({ picture, onClose }) {
  const [url, setUrl] = useState(null);
  const [blob, setBlob] = useState(null);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(null);
  const closeRef = useRef(null);

  useEffect(() => {
    let alive = true;
    let objectUrl = null;
    (async () => {
      try {
        // The host's profile picture is a nice-to-have: without it the pill shows the initial.
        const [imageBlob, avatarBlob] = await Promise.all([
          ImageRightsAPI.pictureFile(picture.image),
          picture.account ? ImageRightsAPI.profilePicture(picture.account) : null,
        ]);
        const png = await renderAppPreview({
          imageBlob,
          avatarBlob,
          logoSrc: schickLogoWhite,
          title: picture.title,
          type: picture.type,
          address: picture.address,
          handle: picture.account,
        });
        if (!alive) return;
        objectUrl = URL.createObjectURL(png);
        setBlob(png);
        setUrl(objectUrl);
      } catch (err) {
        console.error('Vorschau fehlgeschlagen:', err);
        if (alive) setError('Das Bild konnte nicht geladen werden – die Quelle antwortet gerade nicht.');
      }
    })();
    return () => {
      alive = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [picture]);

  useEffect(() => { closeRef.current?.focus(); }, []);
  useEffect(() => {
    const onKey = (event) => { if (event.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const copy = async () => {
    try {
      setCopied((await copyImage(blob)) ? 'ok' : 'unsupported');
    } catch (err) {
      console.error('Kopieren fehlgeschlagen:', err);
      setCopied('unsupported');
    }
  };

  return (
    <>
      <div className="apd-scrim" onClick={onClose} aria-hidden="true" />
      <div className="apd" role="dialog" aria-modal="true" aria-label="App-Vorschau">
        <header className="apd-head">
          <div>
            <h2 className="apd-title">App-Vorschau</h2>
            <p className="apd-sub">So zeigt Schick das Bild oben in der Info-Karte. Kopieren und in die DM einfügen.</p>
          </div>
          <button ref={closeRef} type="button" className="irp-close" onClick={onClose} aria-label="Schließen">✕</button>
        </header>

        <div className="apd-stage">
          {error ? (
            <p className="pp-error">{error}</p>
          ) : url ? (
            <img className="apd-image" src={url} alt={`App-Vorschau von ${picture.title}`} />
          ) : (
            <div className="apd-loading"><div className="pp-spinner" aria-hidden="true" />Vorschau wird erstellt …</div>
          )}
        </div>

        <footer className="apd-actions">
          <button type="button" className="apd-btn apd-btn--primary" onClick={copy} disabled={!blob}>
            {copied === 'ok' ? 'Kopiert ✓' : 'Bild kopieren'}
          </button>
          {url ? (
            <a className="apd-btn" href={url} download={fileName(picture)}>Herunterladen</a>
          ) : (
            <button type="button" className="apd-btn" disabled>Herunterladen</button>
          )}
          {picture.account && (
            <a className="apd-btn" href={`https://ig.me/m/${picture.account}`} target="_blank" rel="noreferrer">DM öffnen</a>
          )}
        </footer>
        {copied === 'ok' && <p className="apd-note">In der DM mit Strg+V (Mac: ⌘+V) einfügen.</p>}
        {copied === 'unsupported' && <p className="apd-note apd-note--warn">Dieser Browser kann keine Bilder kopieren – bitte herunterladen und in der DM anhängen.</p>}
      </div>
    </>
  );
}
